import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import PatientFieldRefPicker from '../../components/forms/PatientFieldRefPicker';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, Save, Trash2, Move } from 'lucide-react';

const TYPES = ['text', 'textarea', 'date', 'number', 'phone', 'checkbox', 'signature'];
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const fid = (f) => f.field_id || f.id;

function Box({ f, selected, onPointerDown }) {
  const b = f.bbox || {};
  const linked = !!f.patient_field_ref;
  return (
    <div
      onPointerDown={(e) => onPointerDown(e, f, 'move')}
      className={`absolute cursor-move select-none transition-colors ${selected ? 'ring-2 ring-[#0E7490] bg-[#0E7490]/15 z-20' : linked ? 'ring-1 ring-[#0E7490]/70 bg-[#0E7490]/5 hover:bg-[#0E7490]/15' : 'ring-1 ring-[#DC2626]/60 bg-[#DC2626]/5 hover:bg-[#DC2626]/10'}`}
      style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%` }}
      title={f.label}
      data-testid={`layout-box-${fid(f)}`}
    >
      {selected && (
        <span
          onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e, f, 'resize'); }}
          className="absolute -right-1.5 -bottom-1.5 w-3 h-3 bg-[#0E7490] rounded-sm cursor-nwse-resize"
          data-testid={`layout-resize-${fid(f)}`}
        />
      )}
    </div>
  );
}

function EditorPage({ idx, img, fields, selectedId, onSelect, onChange }) {
  const ref = useRef(null);
  const start = (e, f, mode) => {
    e.preventDefault();
    onSelect(fid(f));
    const rect = ref.current.getBoundingClientRect();
    const o = { ...f.bbox };
    const sx = e.clientX, sy = e.clientY;
    const move = (ev) => {
      const dx = (ev.clientX - sx) / rect.width, dy = (ev.clientY - sy) / rect.height;
      const nb = mode === 'move'
        ? { ...o, x: clamp(o.x + dx, 0, 1 - o.w), y: clamp(o.y + dy, 0, 1 - o.h) }
        : { ...o, w: clamp(o.w + dx, 0.005, 1 - o.x), h: clamp(o.h + dy, 0.005, 1 - o.y) };
      onChange(fid(f), { bbox: nb });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="bg-white shadow-md mx-auto" style={{ width: '100%', maxWidth: 850 }} data-testid={`layout-page-${idx}`}>
      <div ref={ref} className="relative" onPointerDown={(e) => { if (e.target === e.currentTarget || e.target.tagName === 'IMG') onSelect(null); }}>
        <img src={`data:image/png;base64,${img}`} alt={`Page ${idx + 1}`} className="block w-full h-auto pointer-events-none" draggable={false} />
        {fields.map(f => <Box key={fid(f)} f={f} selected={selectedId === fid(f)} onPointerDown={start} />)}
      </div>
    </div>
  );
}

function FieldPanel({ field, onChange, onDelete }) {
  if (!field) return <p className="text-xs text-[#64748B]">Click a box to edit it. Drag to move, drag the corner to resize, arrow keys nudge (Shift = bigger steps).</p>;
  const b = field.bbox;
  const num = (k) => (
    <label className="text-[11px] text-[#64748B]">{k.toUpperCase()}
      <Input type="number" step="0.1" value={(b[k] * 100).toFixed(1)} onChange={e => onChange({ bbox: { ...b, [k]: clamp(Number(e.target.value) / 100, 0, 1) } })} className="h-8 mt-0.5" data-testid={`layout-${k}-input`} />
    </label>
  );
  return (
    <div className="space-y-3" data-testid="layout-field-panel">
      <div><Label className="text-xs">Label</Label><Input value={field.label} onChange={e => onChange({ label: e.target.value })} data-testid="layout-label-input" /></div>
      <div>
        <Label className="text-xs">Type</Label>
        <Select value={field.field_type} onValueChange={v => onChange({ field_type: v })}>
          <SelectTrigger data-testid="layout-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <PatientFieldRefPicker value={field.patient_field_ref} onChange={v => onChange({ patient_field_ref: v })} testIdPrefix="layout-ref" />
      <div className="grid grid-cols-4 gap-1.5">{['x', 'y', 'w', 'h'].map(num)}</div>
      <Button variant="outline" className="w-full text-[#991B1B] border-[#991B1B]/30" onClick={onDelete} data-testid="layout-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Remove box</Button>
    </div>
  );
}

export default function FormLayoutEditor() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [sel, setSel] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/forms/${formId}`).then(r => {
      setForm(r.data);
      setFields((r.data.fields || []).map(f => ({ ...f, field_id: fid(f), field_type: f.field_type || f.type || 'text' })));
    }).catch(() => toast.error('Could not load form'));
  }, [formId]);

  const update = useCallback((id, patch) => {
    setFields(fs => fs.map(f => (fid(f) === id ? { ...f, ...patch } : f)));
    setDirty(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!sel || ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
      const step = e.shiftKey ? 0.005 : 0.001;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (!d) return;
      e.preventDefault();
      setFields(fs => fs.map(f => (fid(f) === sel ? { ...f, bbox: { ...f.bbox, x: clamp(f.bbox.x + d[0], 0, 1 - f.bbox.w), y: clamp(f.bbox.y + d[1], 0, 1 - f.bbox.h) } } : f)));
      setDirty(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel]);

  const addBox = (page) => {
    const id = `field_${Date.now().toString(36)}`;
    setFields(fs => [...fs, { field_id: id, label: 'New field', field_type: 'text', required: false, page_index: page, bbox: { x: 0.4, y: 0.45, w: 0.2, h: 0.02 }, order: fs.length }]);
    setSel(id);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/forms/${formId}`, { fields: fields.map((f, i) => ({ ...f, order: i })) });
      setDirty(false);
      toast.success('Box positions saved');
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /></div>;
  const selected = fields.find(f => fid(f) === sel);
  const pages = form.source_pdf_pages || [];

  return (
    <div className="min-h-screen bg-[#F1F5F9]" data-testid="form-layout-editor">
      <div className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => { if (!dirty || window.confirm('Discard unsaved box changes?')) navigate(`/forms/builder?id=${formId}`); }} data-testid="layout-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Builder</Button>
          <div>
            <div className="text-sm font-semibold text-[#0A2342] flex items-center gap-1.5"><Move className="w-4 h-4 text-[#0E7490]" />{form.name} — box positions</div>
            <div className="text-[11px] text-[#64748B]">{fields.length} boxes · teal = linked to patient record{dirty ? ' · unsaved changes' : ''}</div>
          </div>
        </div>
        <Button className="careiq-btn-primary" onClick={save} disabled={!dirty || saving} data-testid="layout-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save</Button>
      </div>
      <div className="flex gap-4 p-4">
        <div className="flex-1 space-y-6 min-w-0">
          {pages.map((img, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between max-w-[850px] mx-auto text-xs text-[#64748B]">
                <span>Page {i + 1}</span>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => addBox(i)} data-testid={`layout-add-box-${i}`}><Plus className="w-3.5 h-3.5 mr-1" />Add box</Button>
              </div>
              <EditorPage idx={i} img={img} fields={fields.filter(f => f.bbox && (f.page_index ?? 0) === i)} selectedId={sel} onSelect={setSel} onChange={update} />
            </div>
          ))}
        </div>
        <aside className="w-72 shrink-0 sticky top-20 self-start bg-white rounded-xl border border-[#E2E8F0] p-4">
          <FieldPanel field={selected} onChange={(p) => update(sel, p)} onDelete={() => { setFields(fs => fs.filter(f => fid(f) !== sel)); setSel(null); setDirty(true); }} />
        </aside>
      </div>
    </div>
  );
}
